import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { StatCard } from "../AnchorNew/AnchorDashboard";
import CustomButton from "../Common/CustomButton";
import EllipsisedCell, { formatNumberAndCurrency } from "../Common/EllipsisedCell";
import useFetch from "../Common/useFetch";
import { userContext } from "../Contexts/userContext";
import ReactTable from "../ReactTable";
import UploadCNFilter from "./UploadCNFilter";

export interface WarehouseInvoiceType {
  id: string;
  grnNumber: string;
  invoiceNumber: string;
  warehouseName: string;
  discountAmount: number;
}

const formatDataInvoiceSummary = (data: any) => {
  if (!data) return [];
  return [
    {
      title: "Invoice Paid Early",
      value: formatNumberAndCurrency(data.invoicesPaidEarly, false),
    },
    {
      title: "Total Discount Amount",
      value: formatNumberAndCurrency(data.totalDiscountAmount, true),
    },
  ];
};
const steps = ["Select Warehouse", "Select Invoice", "Upload"];

export default function UploadCreditNote({
  setIsUploadingCreditNote,
  fetchAllData,
}: {
  setIsUploadingCreditNote?: (value: boolean) => void;
  fetchAllData: () => void;
}) {
  const { actor } = useContext(userContext);

  const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>([]);
  const [activeStep, setActiveStep] = React.useState(0);
  const [warehouseOptions, setWarehouseOptions] = useState<string[]>([]);

  const [warehouseInvoices, setWarehouseInvoices] = useState<WarehouseInvoiceType[]>([]);
  const [isLoadingWarehouseInvoices, setIsLoadingWarehouseInvoices] = useState<boolean>(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({
    grnNumber: [],
    invoiceNumber: [],
    warehouseName: [],
  });

  // Create filter options from unique values in warehouseInvoices
  const grnNumberOptions = useMemo(
    () => Array.from(new Set(warehouseInvoices.map((inv) => inv.grnNumber))).map((grn) => ({ grnNumber: grn })),
    [warehouseInvoices]
  );
  const invoiceNumberOptions = useMemo(
    () => Array.from(new Set(warehouseInvoices.map((inv) => inv.invoiceNumber))).map((inv) => ({ invoiceNumber: inv })),
    [warehouseInvoices]
  );
  const warehouseNameOptions = useMemo(
    () => Array.from(new Set(warehouseInvoices.map((inv) => inv.warehouseName))).map((wh) => ({ warehouseName: wh })),
    [warehouseInvoices]
  );

  // Filter warehouseInvoices based on appliedFilters
  const filteredWarehouseInvoices = useMemo(() => {
    return warehouseInvoices.filter((invoice) => {
      const matchesGrn = appliedFilters.grnNumber.length === 0 || appliedFilters.grnNumber.includes(invoice.grnNumber);
      const matchesInvoice =
        appliedFilters.invoiceNumber.length === 0 || appliedFilters.invoiceNumber.includes(invoice.invoiceNumber);
      const matchesWarehouse =
        appliedFilters.warehouseName.length === 0 || appliedFilters.warehouseName.includes(invoice.warehouseName);
      return matchesGrn && matchesInvoice && matchesWarehouse;
    });
  }, [warehouseInvoices, appliedFilters]);

  const selectedInvoices = filteredWarehouseInvoices.filter((row) => row.id in rowSelection);
  const totalAmount = selectedInvoices.reduce((acc, row) => acc + row.discountAmount, 0);

  // Calculate invoices summary from filteredWarehouseInvoices
  const invoicesSummary = useMemo(() => {
    return {
      invoicesPaidEarly: filteredWarehouseInvoices.length,
      totalDiscountAmount: Math.round(filteredWarehouseInvoices.reduce((acc, inv) => acc + inv.discountAmount, 0)),
    };
  }, [filteredWarehouseInvoices]);

  const invoiceSummaryCards = formatDataInvoiceSummary(invoicesSummary);
  const invoiceSummaryCardCount = invoiceSummaryCards.length;
  const invoiceSummaryCardSize = invoiceSummaryCardCount >= 5 ? 12 / 5 : 12 / invoiceSummaryCardCount;

  useEffect(() => {
    getWarehouseOptions();
  }, []);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setIsUploading(true);

    if (file) {
      const bodyformData = new FormData();
      bodyformData.append("file", file);
      bodyformData.append("invoiceIds", JSON.stringify(selectedInvoices.map((invoice) => invoice.id)));
      bodyformData.append("fileName", file.name);
      await useFetch(`/invoices/credit-notes/upload/2/${actor.actorId}`, "POST", {
        data: bodyformData,
        failureMessage: "Failed to upload credit note",
        showSuccessToast: true,
        thenCallBack: (res) => {
          setIsUploading(false);
          if (res.data.result.verificationResult.requiresManualReview) {
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
            return;
          } else {
            setIsUploadingCreditNote(false);
            fetchAllData();
          }

          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        },
        catchCallBack: () => {
          setIsUploading(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        },
      });
    }
  };

  const getWarehouseOptions = async () => {
    await useFetch(`/invoices/credit-notes/warehouses/2/${actor.actorId}`, "GET", {
      failureMessage: "Failed to fetch warehouse options",
      thenCallBack: (response) => {
        const warehouses = response.data.result.warehouses;
        setWarehouseOptions(warehouses || []);
      },
      errorCallback: (error) => {
        console.error("Error fetching warehouse options:", error);
      },
    });
  };

  const invoiceColumns = useMemo<MRT_ColumnDef<WarehouseInvoiceType>[]>(
    () => [
      {
        header: "GRN number",
        accessorKey: "grnNumber",
        minSize: 300,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Invoice number",
        accessorKey: "invoiceNumber",
        minSize: 300,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Warehouse name",
        accessorKey: "warehouseName",
        minSize: 300,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Discount amount",
        accessorKey: "discountAmount",
        minSize: 300,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
    ],
    []
  );

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    if (activeStep === 0) {
      getWarehouseInvoices();
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      setIsUploadingCreditNote(false);
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getWarehouseInvoices = async () => {
    setIsLoadingWarehouseInvoices(true);
    await useFetch(`/invoices/credit-notes/invoices/2/${actor.actorId}`, "POST", {
      data: {
        warehouses: selectedWarehouses,
      },
      failureMessage: "Failed to fetch warehouse invoices",
      thenCallBack: (response) => {
        setWarehouseInvoices(response.data.result.invoices || []);
        setIsLoadingWarehouseInvoices(false);
      },
      catchCallBack: (error) => {
        console.error("Error fetching warehouse invoices", error);
        setIsLoadingWarehouseInvoices(false);
      },
    });
  };

  const handleDownloadInvoices = async () => {
    setIsDownloading(true);
    await useFetch(`/invoices/credit-notes/invoices/2/${actor.actorId}`, "POST", {
      data: {
        warehouses: warehouseOptions,
      },
      failureMessage: "Failed to download invoices",
      thenCallBack: (response) => {
        const invoices = response.data.result.invoices || [];
        if (invoices.length > 0) {
          generateAndDownloadExcel(invoices);
        } else {
          setIsDownloading(false);
        }
      },
      catchCallBack: (error) => {
        console.error("Error downloading invoices", error);
        setIsDownloading(false);
      },
    });
  };

  const generateAndDownloadExcel = async (invoices: WarehouseInvoiceType[]) => {
    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Invoices");

      // Define columns
      worksheet.columns = [
        { header: "GRN Number", key: "grnNumber", width: 20 },
        { header: "Invoice Number", key: "invoiceNumber", width: 20 },
        { header: "Warehouse Name", key: "warehouseName", width: 30 },
        { header: "Discount Amount", key: "discountAmount", width: 20 },
      ];

      // Style header row
      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };

      // Add data rows
      invoices.forEach((invoice) => {
        worksheet.addRow({
          grnNumber: invoice.grnNumber,
          invoiceNumber: invoice.invoiceNumber,
          warehouseName: invoice.warehouseName,
          discountAmount: invoice.discountAmount,
        });
      });

      // Generate buffer and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `invoices_${new Date().toISOString().split("T")[0]}.xlsx`);
      setIsDownloading(false);
    } catch (error) {
      console.error("Error generating Excel file:", error);
      setIsDownloading(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ bgcolor: "#fff", mb: 2, mt: 1, p: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, ml: 1 }}>
          Upload Credit note
        </Typography>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => {
            const stepProps: { completed?: boolean } = {};

            return (
              <Step key={label} {...stepProps}>
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontSize: "14px",
                      fontWeight: 600,
                    },
                    "& .MuiStepLabel-label.Mui-active": {
                      fontSize: "14px",
                      fontWeight: 600,
                    },
                    "& .MuiStepLabel-label.Mui-completed": {
                      fontSize: "14px",
                      fontWeight: 600,
                    },
                  }}
                  StepIconProps={{
                    sx: {
                      "&.Mui-active": {
                        color: "#541C4C", // Active step icon background
                      },
                      "&.Mui-completed": {
                        color: "#541C4C", // Completed step icon background
                      },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      <>
        <Box sx={{ height: "calc(90vh - 200px)", overflowY: "auto", mb: 7 }}>
          {activeStep === 0 && (
            <Box sx={{ p: 3, bgcolor: "#fff", width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Step 1 - Select Warehouse
                </Typography>
                <CustomButton
                  variant="contained"
                  onClick={handleDownloadInvoices}
                  disabled={isDownloading}
                  startIcon={isDownloading ? <CircularProgress size={20} /> : null}
                >
                  Download All Data
                </CustomButton>
              </Box>
              <Autocomplete
                fullWidth
                multiple
                disablePortal
                value={selectedWarehouses}
                onChange={(_, value) => setSelectedWarehouses(value)}
                // options={warehouseOptions}
                options={warehouseOptions}
                renderInput={(params) => <TextField fullWidth {...params} label="Select Warehouse" />}
              />
            </Box>
          )}
          {activeStep === 1 && (
            <Box sx={{ p: 2, bgcolor: "#fff", width: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: 20,
                      fontWeight: 600,
                    }}
                  >
                    Step 2 - Select Invoice
                  </Typography>
                  <Typography color="text.secondary" mb={2}>
                    Select invoices to upload credit note
                  </Typography>
                </Box>
                <CustomButton
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => {
                    setOpenFilter(true);
                  }}
                >
                  Filter
                </CustomButton>
              </Box>
              <Grid container spacing={2} mb={4}>
                {invoiceSummaryCards.map(({ title, value }, index) => (
                  <Grid key={index} item xs={12} sm={6} md={invoiceSummaryCardSize}>
                    <StatCard title={title} value={value} />
                  </Grid>
                ))}
              </Grid>
              <ReactTable
                data={filteredWarehouseInvoices}
                getRowId={(originalRow) => originalRow.id}
                columns={invoiceColumns}
                enableRowSelection={true}
                additionalState={{ rowSelection }}
                onRowSelectionChange={setRowSelection}
                isLoading={isLoadingWarehouseInvoices}
                enableSelectAll={true}
                enableRowVirtualization={true}
                enableColumnVirtualization={true}
              />
            </Box>
          )}
          {activeStep === 2 && (
            <Box sx={{ p: 3, bgcolor: "#fff" }}>
              {/* Step Title */}
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Step 3- Upload
              </Typography>

              {/* Table */}
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  mb: 3,
                  maxHeight: "55vh", // height of scrollable area
                  overflow: "auto",
                }}
              >
                <Table stickyHeader={true}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          bgcolor: "#FAFAFA",
                        }}
                      >
                        Invoice Number
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          bgcolor: "#FAFAFA",
                        }}
                      >
                        Invoice Discount Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedInvoices.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.invoiceNumber}</TableCell>
                        <TableCell>₹ {row.discountAmount.toLocaleString("en-IN")}</TableCell>
                      </TableRow>
                    ))}

                    {/* Total Row */}
                    <TableRow
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        backgroundColor: "white",
                        zIndex: 1,
                      }}
                    >
                      <TableCell sx={{ fontWeight: "bold", bgcolor: "#FAFAFA" }}>Total Amount</TableCell>
                      <TableCell sx={{ fontWeight: "bold", bgcolor: "#FAFAFA" }}>
                        ₹ {totalAmount.toLocaleString("en-IN")}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Hidden file input */}
              <input
                type="file"
                multiple={false}
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            bgcolor: "#fff",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            p: 2,
          }}
        >
          <CustomButton variant="outlined" onClick={handleBack} sx={{ mr: 1 }}>
            Previous
          </CustomButton>
          <Box sx={{ flex: "1 1 auto" }} />

          <CustomButton
            variant="contained"
            disabled={
              selectedWarehouses.length === 0 ||
              (activeStep === 1 && Object.keys(rowSelection).length === 0) ||
              (activeStep === 2 && isUploading)
            }
            onClick={() => {
              if (activeStep === 2) {
                fileInputRef.current?.click();
              } else {
                handleNext();
              }
            }}
            startIcon={activeStep === 2 && isUploading ? <CircularProgress size={20} /> : null}
          >
            {activeStep === 2 ? "Upload Credit Note" : "Next"}
          </CustomButton>
        </Box>
      </>
      {openFilter && (
        <UploadCNFilter
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          setAppliedFilters={setAppliedFilters}
          appliedFilters={appliedFilters}
          grnNumberOptions={grnNumberOptions}
          invoiceNumberOptions={invoiceNumberOptions}
          warehouseNameOptions={warehouseNameOptions}
        />
      )}
    </Box>
  );
}
